import { GetServerSideProps, InferGetServerSidePropsType } from 'next';

import config from 'config';
import withShopContext from 'core/hocs/withShopContext';
import withPageLayout from 'core/hocs/withPageLayout';
import withDescriptionData from 'core/hocs/withDescriptionData';
import { ArticleCategory, ArticlePreview } from 'types/models/Article';
import { GetPageLayoutResult, serverRequests as pageLayoutServerRequests } from '@layouts/PageLayout/PageLayout.utils';
import ArticlesPage, { getBreadcrumbs } from '@pages/Articles';
import {
  articleCategoryRequestConfig,
  GetArticleCategoryResult,
  GetArticleCategoryVariables,
} from 'graphql/queries/GetArticleCategory.query';
import {
  articleCategoriesRequestConfig,
  GetArticleCategoriesResult,
  GetArticleCategoriesVariables,
} from 'graphql/queries/GetArticleCategories.query';
import { articlesRequestConfig, GetArticlesResult, GetArticlesVariables } from 'graphql/queries/GetArticles.query';
import graphQLRequest from 'utils/graphQLRequest';
import combineRequests, { RequestConfig } from 'utils/combineRequest';
import handleError from 'utils/handleError';
import { startTransaction } from 'utils/startTransaction';
import handleNotFoundError from 'utils/handleNotFoundError';
import omit from 'utils/omit';

const { routes } = config;

const serverRequests: RequestConfig[] = [
  ...pageLayoutServerRequests,
  articleCategoryRequestConfig,
  articlesRequestConfig,
  articleCategoriesRequestConfig,
];

type Result = GetPageLayoutResult &
  GetArticlesResult & {
    articleCategory: ArticleCategory;
    mainArticle: ArticlePreview | null;
    tagId?: number;
    tagSlug?: string;
    page: number;
    total: number;
  };

const getArticlesWhere = (category: string, tag?: string): GetArticlesVariables['articlesWhere'] => {
  const where: GetArticlesVariables['articlesWhere'] = {
    is_hidden: { _eq: false },
    _or: [{ published_at: { _is_null: true } }, { published_at: { _lt: 'now' } }],
    tags: { article_tag: { category: { slug: { _eq: category } } } },
  };

  if (tag) {
    where.tags.article_tag.slug = { _eq: tag };
  }

  return where;
};

export const getServerSideProps: GetServerSideProps<Result> = withShopContext(
  withDescriptionData(
    async ({ res, params, query }, { client, shopId }) => {
      const category = params?.category as string;
      const tagSlug = params?.tag as string;
      const page = Number(query?.page) || 1;

      const transaction = startTransaction({ op: 'server.requests', name: routes.articles.category.href });
      const combinedDataSpan = transaction.startChild({ op: 'combined-data' });
      const { data: combinedData } = await graphQLRequest<
        GetPageLayoutResult & GetArticleCategoryResult & GetArticlesResult & GetArticleCategoriesResult,
        GetArticleCategoryVariables & GetArticlesVariables & GetArticleCategoriesVariables
      >(client, combineRequests(serverRequests), {
        variables: {
          category,
          articlesWhere: getArticlesWhere(category, tagSlug),
          offset: 8 * (page - 1) + 1,
        },
      });
      combinedDataSpan.finish();

      if (!combinedData) {
        transaction.finish();
        return handleError(res, 500);
      }

      const {
        allArticlesCategories,
        articleCategories: [articleCategory],
      } = combinedData;

      if (!articleCategory) {
        transaction.finish();
        return handleNotFoundError(client, res, shopId);
      }

      const tag = tagSlug ? articleCategory.tags.find(({ slug }) => slug === tagSlug) : undefined;

      if (tagSlug && !tag) {
        transaction.finish();
        return handleNotFoundError(client, res, shopId);
      }

      if (tag) {
        articleCategory.seoData = tag.seoData;
      }

      transaction.finish();

      return {
        props: {
          ...omit(combinedData, ['articleCategories', 'allArticlesCategories', 'mainArticles', 'articlesAggregate']),
          mainArticle: combinedData.mainArticles[0] || null,
          tagId: tag?.id || null,
          tagSlug,
          total: Math.ceil(((combinedData?.articlesAggregate.aggregate.count || 1) - 1) / 8),
          page,
          articleCategory,
          breadcrumbs: getBreadcrumbs(allArticlesCategories, articleCategory.tags, articleCategory, tag),
        },
      };
    },
    {
      descriptionsGetter: ({ articleCategory, articles }) => [
        articleCategory.seoData?.description,
        ...articles.map((article) => article.teaser),
      ],
    }
  )
);

const Articles = ({
  articleCategory,
  mainArticle,
  articles,
  total,
  tagId,
  tagSlug,
  page,
}: InferGetServerSidePropsType<typeof getServerSideProps>): JSX.Element => (
  <ArticlesPage
    {...articleCategory}
    mainArticle={mainArticle}
    articles={articles}
    total={total}
    tagId={tagId}
    tagSlug={tagSlug}
    page={page}
  />
);

export default withPageLayout(Articles, {
  showBreadcrumb: true,
  breadcrumbVariant: 'articles',
  breadcrumbLinkVariant: 'real',
});
